import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ManualInstallDialogComponent } from '../other/manual-install-dialog/manual-install-dialog.component';
import { ManualInstallDialogDeniedComponent } from '../other/manual-install-dialog-denied/manual-install-dialog-denied.component';

import Bowser from 'bowser';
import { LoggerService } from './logger.service';


export enum DeviceType {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop',
}

export enum BrowserName {
  Chrome = 'chrome',
  Edge = 'edge',
  Opera = 'opera',
  Samsung = 'samsung',
  Brave = 'brave',
  Safari = 'safari',
  Firefox = 'firefox',
  InternetExplorer = 'internet explorer',
  Unknown = 'unknown',
}

export enum InstallMethod {
  Auto = 'auto',
  Manual = 'manual',
  Unsupported = 'unsupported',
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  #isAppInstalledSubject = new BehaviorSubject<boolean>(true);
  isAppInstalled$ = this.#isAppInstalledSubject.asObservable();
  #isUsingBrowserSubject = new BehaviorSubject<boolean>(true);
  isUsingBrowser$ = this.#isUsingBrowserSubject.asObservable();
  #installMethodSubject = new BehaviorSubject<InstallMethod>(InstallMethod.Manual);
  installMethod$ = this.#installMethodSubject.asObservable();

  private userAgent: string = '';
  private browserName: BrowserName = BrowserName.Unknown;
  private deviceType: DeviceType = DeviceType.Desktop;
  private isIOS: boolean = false;

  private browser: any;
  private deferredPrompt: any;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object, 
    private logger: LoggerService,
    private dialog: MatDialog) {
    if (isPlatformBrowser(this.platformId)) {
      this.browser = Bowser.getParser(navigator.userAgent);
      this.#setUserAgent();
      this.#setIsIOS();

      this.#setBrowserName();
      this.#setDeviceType();

      this.#checkIfUsingBrowser();
      this.#detectInstallMethod();

      window.addEventListener('beforeinstallprompt', (event) => {
        this.logger.log('beforeinstallprompt fired');
        this.#isAppInstalledSubject.next(false);
        event.preventDefault();
        this.deferredPrompt = event;
        this.#installMethodSubject.next(InstallMethod.Auto);
      });

      window.addEventListener('appinstalled', () => {
        this.logger.log('PWA wurde installiert');
        this.#isAppInstalledSubject.next(true);
        this.#isUsingBrowserSubject.next(false);
      });
    }
  }

  // ### Public Methods ###
  public showManualInstallInstructions() {
    this.logger.log(`showManualInstallInstructions called with: deviceType: ${this.deviceType}, browserName: ${this.browserName}, isIOS: ${this.isIOS}`);
    this.dialog.open(ManualInstallDialogComponent, {
      height: '80vh',
      maxHeight: '600px',
      hasBackdrop: true,
      data: { deviceType: this.deviceType, browserName: this.browserName, isIOS: this.isIOS }
    });
  }

  public showInstallDeniedMessage() {
    this.logger.log(`showInstallDeniedMessage called with deviceType: ${this.deviceType}, browserName: ${this.browserName}, isIOS: ${this.isIOS}`);
    this.dialog.open(ManualInstallDialogDeniedComponent, {
      hasBackdrop: true,
    });
  }

  public installPWA() {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((choiceResult: { outcome: string; }) => {
        if (choiceResult.outcome === 'accepted') {
          this.logger.log('User accepted the A2HS prompt');
        } else {
          this.logger.log('User dismissed the A2HS prompt');
        }
        this.deferredPrompt = null;
      });
    }
  }

  public getAllData(): { browserName: BrowserName | null, deviceType: DeviceType, isIOS: boolean } {
    return {
      browserName: this.browserName,
      deviceType: this.deviceType,
      isIOS: this.isIOS,
    };
  }

  // ### Private Methods ###
  #setUserAgent(): void {
    this.userAgent = navigator.userAgent;
  }

  #setIsIOS() {
    this.isIOS = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(this.userAgent)
      // iPad on iOS 13 detection
      || (this.userAgent.includes("Mac") && "ontouchend" in document)
  }

  #setBrowserName(): void {
    const browserName = this.browser.getBrowserName();

    switch (browserName) {
      case 'Chrome':
        this.browserName = BrowserName.Chrome;
        break;
      case 'Safari':
        this.browserName = BrowserName.Safari;
        break;
      case 'Firefox':
        this.browserName = BrowserName.Firefox;
        break;
      case 'Microsoft Edge':
        this.browserName = BrowserName.Edge;
        break;
      case 'Internet Explorer':
        this.browserName = BrowserName.InternetExplorer;
        break;
      case 'Opera':
        this.browserName = BrowserName.Opera;
        break;
      case 'Samsung Internet for Android':
        this.browserName = BrowserName.Samsung;
        break;
      default:
        this.browserName = BrowserName.Unknown;
    }
  }

  #setDeviceType(): void {
    const platformType = this.browser.getPlatformType();

    switch (platformType) {
      case 'mobile':
        this.deviceType = DeviceType.Mobile;
        break;
      case 'tablet':
        this.deviceType = DeviceType.Tablet;
        break;
      case 'desktop':
        if (this.isIOS) {
          // tablets with iOS 13 and higher are considered as desktop in bowser api, hence we need to check if it is an iPad via userAgent
          this.deviceType = DeviceType.Tablet;
        }
        else {
          this.deviceType = DeviceType.Desktop;
        }
        break;
      default:
        this.deviceType = DeviceType.Desktop;
    }
  }


  #checkIfUsingBrowser() {
    if (isPlatformBrowser(this.platformId)) {
      if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone) {
        this.logger.log('App wird im Vollbildmodus ausgeführt');
        this.#isUsingBrowserSubject.next(false);
      } else {
        this.logger.log('App wird im Browser ausgeführt');
        this.#isUsingBrowserSubject.next(true);
      }
    }
  }

  #detectInstallMethod(): void {
    this.#installMethodSubject.next(InstallMethod.Unsupported);

    if (this.deviceType === DeviceType.Desktop) {
      this.#setInstallMethodForDesktop(this.browserName);
    } else {
      this.#setInstallMethodForMobile(this.browserName);
    }

  }

  #setInstallMethodForDesktop(browserName: BrowserName): void {
    switch (browserName) {
      case BrowserName.Chrome:
      case BrowserName.Edge:
      case BrowserName.Opera:
      case BrowserName.Samsung:
      case BrowserName.Brave:
        this.#installMethodSubject.next(InstallMethod.Auto);
        break;
      case BrowserName.Safari:
      case BrowserName.Firefox:
        this.#installMethodSubject.next(InstallMethod.Unsupported);
        this.#isAppInstalledSubject.next(false);
        break;
      default:
        this.#installMethodSubject.next(InstallMethod.Unsupported);
        this.#isAppInstalledSubject.next(false);
        break;
    }
  }

  #setInstallMethodForMobile(browserName: BrowserName): void {
    // Mobile und Tablet
    switch (browserName) {
      case BrowserName.Safari:
      case BrowserName.Firefox:
        this.#installMethodSubject.next(InstallMethod.Manual); // Safari und Firefox
        this.#isAppInstalledSubject.next(false);
        break;
      case BrowserName.Chrome:
      case BrowserName.Edge:
      case BrowserName.Opera:
      case BrowserName.Samsung:
      case BrowserName.Brave:
        if (this.isIOS) {
          this.#installMethodSubject.next(InstallMethod.Manual); // iOS ab 16.4
          this.#isAppInstalledSubject.next(false);
        } else {
          this.#installMethodSubject.next(InstallMethod.Auto); // Android
        }
        break;
      default:
        this.#installMethodSubject.next(InstallMethod.Unsupported); // Andere mobile Browser
        this.#isAppInstalledSubject.next(false);
        break;
    }
  }
}